import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

function UhcAuditReports({ fetchWithAuth }) {
  const [latestAudit, setLatestAudit] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showFindings, setShowFindings] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [auditRes, historyRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/uhc/audit`),
        fetchWithAuth(`${API_BASE}/api/uhc/audit/history`),
      ]);
      if (auditRes.ok) {
        const data = await auditRes.json();
        setLatestAudit(data.audit);
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load audit data:', err);
    }
    setLoading(false);
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/uhc/audit/run`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setLatestAudit(data.audit);
        loadData();
      }
    } catch (err) {
      console.error('Failed to run audit:', err);
    }
    setRunning(false);
  };

  const clearHistory = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/uhc/audit/history`, { method: 'DELETE' });
      if (res.ok) {
        setHistory([]);
        setLatestAudit(null);
      }
    } catch (err) {
      console.error('Failed to clear audit history:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--status-success, #2E7D32)';
    if (score >= 60) return 'var(--brand-gold, #C9A962)';
    return 'var(--status-error, #C62828)';
  };

  const getSeverityClass = (severity) => {
    if (severity === 'warning') return 'uhca-finding--warning';
    if (severity === 'error') return 'uhca-finding--error';
    return 'uhca-finding--info';
  };

  if (loading) {
    return <div className="card"><p>Loading audit data...</p></div>;
  }

  return (
    <div className="uhca-container">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">UHC Audit Reports</h3>
          <div className="uhca-headerActions">
            <button
              className="btn btn-primary btn-sm"
              onClick={runAudit}
              disabled={running}
            >
              {running ? 'Running...' : 'Run Audit'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={clearHistory}
            >
              Clear History
            </button>
          </div>
        </div>

        <div className="uhca-explainer">
          <p>
            The UHC Audit evaluates the quality of health data that feeds into your AI recommendations.
            A higher score means cleaner, more complete data — leading to better, safer AI recommendations.
            Click <strong>Run Audit</strong> to generate or refresh the audit.
          </p>
        </div>

        {!latestAudit ? (
          <p className="uhca-emptyState">No audit results yet. Click "Run Audit" to generate one.</p>
        ) : (
          <>
            {latestAudit.safety_conflicts > 0 && (
              <div className="uhca-safetyAlert">
                <strong>Safety Alert:</strong> {latestAudit.safety_conflicts} drug-allergy conflict{latestAudit.safety_conflicts !== 1 ? 's' : ''} detected.
                An active medication may conflict with a documented allergy. Review immediately.
              </div>
            )}
            <div className="uhca-scoreCard">
              <div className="uhca-scoreCircle" style={{ borderColor: getScoreColor(latestAudit.audit_score) }}>
                <span className="uhca-scoreValue" style={{ color: getScoreColor(latestAudit.audit_score) }}>
                  {latestAudit.audit_score}
                </span>
                <span className="uhca-scoreLabel">/ 100</span>
              </div>
              <div className="uhca-scoreDetails">
                <div className="uhca-metricsGroup">
                  <h4 className="uhca-groupTitle">Included Records</h4>
                  <div className="uhca-metricsRow">
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">Total Records</span>
                      <span className="uhca-metricValue">{latestAudit.total_records}</span>
                      <span className="uhca-metricHelp">Total health records imported from all sources</span>
                    </div>
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">With Description</span>
                      <span className="uhca-metricValue">
                        {latestAudit.records_with_description}
                        {latestAudit.total_records > 0 && (
                          <span className="uhca-metricPct">
                            ({Math.round((latestAudit.records_with_description / latestAudit.total_records) * 100)}%)
                          </span>
                        )}
                      </span>
                      <span className="uhca-metricHelp">Records with clinical narrative the AI can interpret</span>
                    </div>
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">With Dates</span>
                      <span className="uhca-metricValue">
                        {latestAudit.records_with_dates}
                        {latestAudit.total_records > 0 && (
                          <span className="uhca-metricPct">
                            ({Math.round((latestAudit.records_with_dates / latestAudit.total_records) * 100)}%)
                          </span>
                        )}
                      </span>
                      <span className="uhca-metricHelp">Records with dates for accurate timeline tracking</span>
                    </div>
                  </div>
                </div>
                <div className="uhca-metricsGroup uhca-metricsGroup--excluded">
                  <h4 className="uhca-groupTitle">Excluded Records</h4>
                  <div className="uhca-metricsRow">
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">Duplicates</span>
                      <span className="uhca-metricValue">{latestAudit.duplicate_count}</span>
                      <span className="uhca-metricHelp">Repeated records that waste AI processing</span>
                    </div>
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">Flagged</span>
                      <span className="uhca-metricValue">{latestAudit.flagged_count}</span>
                      <span className="uhca-metricHelp">Records with quality issues (garbage data, missing info)</span>
                    </div>
                    <div className="uhca-metric">
                      <span className="uhca-metricLabel">Other Excluded</span>
                      <span className="uhca-metricValue">{latestAudit.excluded_count}</span>
                      <span className="uhca-metricHelp">Records with little/no clinical AI value</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {latestAudit.findings && latestAudit.findings.length > 0 && (() => {
              const excludedTotal = (latestAudit.duplicate_count || 0) + (latestAudit.flagged_count || 0) + (latestAudit.excluded_count || 0);
              return (
              <div className="uhca-findingsSection">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowFindings(!showFindings)}
                >
                  {showFindings ? 'Hide Excluded Records' : 'Show Excluded Records (' + excludedTotal + ')'}
                </button>
                {showFindings && (
                  <div className="uhca-findingsList">
                    {latestAudit.findings.map((f, i) => (
                      <div key={i} className={`uhca-finding ${getSeverityClass(f.severity)}`}>
                        <span className="uhca-findingType">{f.record_type}</span>
                        <span className="uhca-findingIssue">{f.issue}</span>
                        <span className="uhca-findingDetail">{f.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })()}
          </>
        )}
      </div>

      {history.length > 1 && (
        <div className="card uhca-historyCard">
          <div className="card-header">
            <h3 className="card-title">Audit History</h3>
          </div>
          <table className="uhca-historyTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Score</th>
                <th>Records</th>
                <th>Flagged</th>
                <th>Dupes</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}</td>
                  <td style={{ color: getScoreColor(h.audit_score), fontWeight: 600 }}>
                    {h.audit_score}
                  </td>
                  <td>{h.total_records}</td>
                  <td>{h.flagged_count}</td>
                  <td>{h.duplicate_count}</td>
                  <td>{h.trigger_source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UhcAuditReports;
